import { Badge } from '../ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { FileCheck, AlertCircle, Clock, HelpCircle } from 'lucide-react';
import { SCORE_SOURCE, SCORE_SOURCE_CONFIG } from '../../utils/scoreSourceUtils';
import { cn } from '../../lib/utils';

/**
 * ScoreSourceBadge - Displays the data source indicator for Fit Score states
 * 
 * Props:
 * - source: SCORE_SOURCE value (PREREQUISITES_INCOMPLETE, ANALYSIS_REQUIRED, AI_ANALYZED)
 * - compact: boolean - Use shorter label
 * - showTooltip: boolean - Show tooltip on hover (default: true)
 * - className: string - Additional styling
 */
export default function ScoreSourceBadge({ 
  source, 
  compact = false, 
  showTooltip = true,
  className = '' 
}) {
  const config = SCORE_SOURCE_CONFIG[source] || SCORE_SOURCE_CONFIG[SCORE_SOURCE.PREREQUISITES_INCOMPLETE];
  
  const Icon = source === SCORE_SOURCE.AI_ANALYZED ? FileCheck : 
               source === SCORE_SOURCE.ANALYSIS_REQUIRED ? Clock : 
               AlertCircle;
  
  const BadgeContent = (
    <Badge 
      className={cn(
        "text-xs font-medium border",
        config.bgColor,
        config.textColor,
        config.borderColor,
        className
      )}
      data-testid={`score-source-badge-${source?.toLowerCase()?.replace(/_/g, '-')}`}
    >
      <Icon className="w-3 h-3 mr-1" />
      {compact ? config.shortLabel : config.label}
    </Badge>
  );

  if (!showTooltip) {
    return BadgeContent;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {BadgeContent}
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="text-sm">{config.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * ScoreDisplay - Combined score value with source badge
 */
export function ScoreDisplay({ 
  score, 
  source, 
  label = "Fit Score",
  size = 'md',
  showBadge = true,
  className = ''
}) {
  const sizeClasses = {
    sm: { text: 'text-xl', label: 'text-xs' },
    md: { text: 'text-3xl', label: 'text-sm' },
    lg: { text: 'text-4xl', label: 'text-base' }
  };

  const getScoreColor = (s) => {
    if (s === null || s === undefined) return 'text-slate-400';
    if (s >= 70) return 'text-emerald-600';
    if (s >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  return (
    <div className={cn("flex flex-col", className)}>
      <div className="flex items-center gap-2 mb-1">
        <span className={cn("text-slate-600 font-medium uppercase tracking-wider", sizeClasses[size].label)}>
          {label}
        </span>
        {showBadge && source && (
          <ScoreSourceBadge source={source} compact={true} />
        )}
      </div>
      <div className="flex items-baseline gap-1">
        <span className={cn("font-bold", sizeClasses[size].text, getScoreColor(score))}>
          {score !== null && score !== undefined ? `${score}%` : '--'}
        </span>
      </div>
    </div>
  );
}

/**
 * AnalysisRequiredNotice - Banner explaining that AI Analysis is needed
 */
export function AnalysisRequiredNotice({ className = '' }) {
  return (
    <div className={cn(
      "flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg",
      className
    )}>
      <Clock className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-medium text-amber-800">Analysis Required</p>
        <p className="text-xs text-amber-700 mt-1">
          Prerequisites are met. Run AI Analysis on the Ingestion Hub to calculate the Fit Score. 
          The score measures alignment between RFP requirements and your team's capabilities.
        </p>
      </div>
    </div>
  );
}

/**
 * PreliminaryScoreNotice - kept for backward compat, now shows AnalysisRequiredNotice
 */
export function PreliminaryScoreNotice({ className = '' }) {
  return <AnalysisRequiredNotice className={className} />;
}
